import {Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {ConstantsService} from '../../../../services/constants.service';
import {PropertyService} from '../services/property.service';
import {Property} from '../../../../types/admin-data.types';
import {UrlService} from '../../../../services/url.service';
import {ApiClientService} from '../../../../services/api-client.service';
import {Http} from '@angular/http';
import {Subject} from 'rxjs/Subject';
import {debounceTime, distinctUntilChanged, finalize, map, switchMap, tap} from 'rxjs/operators';
import {Observable} from 'rxjs/Observable';

declare const swal;
declare const toastr;
declare const $;

@Component({
    selector: 'property-modal',
    templateUrl: './property-modal.component.html',
    styleUrls: ['./property-modal.component.scss']
})
export class PropertyModalComponent implements OnInit, OnDestroy {
    tabId: string = '';
    selectedItems = [];
    dropdownSettings = {};
    $destroy = new Subject<void>();
    @Output() onClose = new EventEmitter();
    @ViewChild('form') form: NgForm;
    public data: Property = new Property();

    public disabledSubmit = false;
    public isEditMode = false;
    public multiselectTouched = false;
    public contactsTouched = false;
    public addressSuggestions = [];
    public addressValueChanged: Subject<string> = new Subject<string>();
    public addressTimeout = null;
    corporationNumberSearchSubject = new Subject<string>();
    foundCorporationNumbers: Observable<{ value: any, optionText: string }[]>;
    loadingCorporations: boolean;

    constructor(public api: ApiClientService,
                public urlService: UrlService,
                public propertyService: PropertyService,
                private http: Http) {
        this.foundCorporationNumbers = this.corporationNumberSearchSubject
            .pipe(
                distinctUntilChanged(),
                tap(() => this.loadingCorporations = true),
                switchMap(
                    term => this.propertyService.getCorporationNumberForSelect(term)
                        .pipe(
                            tap(({total}) => this.loadingCorporations = total === undefined),
                            map(({entities}) => entities)
                        )
                ),
                finalize(() => this.loadingCorporations = false)
            );
    }

    ngOnInit() {
        this.tabId = this.propertyService.urlService.queryParams['tab'];
        this.dropdownSettings = {
            singleSelection: false,
            idField: 'id',
            textField: 'name',
            selectAllText: 'Select All',
            unSelectAllText: 'UnSelect All',
            allowSearchFilter: true
        };
        // this.tabId == 'property' ? this.propertyService.getCorporationNumberList() : null;
        if (this.tabId === 'propertyGroup' && this.propertyService.currentCorporationNumber && this.propertyService.currentCorporationNumber.id) {
            this.data.name = this.propertyService.currentCorporationNumber.name;
            this.data.companyId = this.propertyService.currentCorporationNumber.companyId;
            this.isEditMode = true;
        }
        this.data.contacts = [];
        this.urlService.modalOpened.emit();

        this.addressValueChanged
            .pipe(
                debounceTime(1000),
                distinctUntilChanged()
            )
            .subscribe(newText => this.getAddress(newText));
    }

    onItemSelect(item: any) {
        this.multiselectTouched = true;
    }

    onSelectAll(items: any) {
        this.multiselectTouched = true;
    }

    close(form: NgForm) {
        if (form.dirty && (form.touched || this.contactsTouched || this.multiselectTouched)) {
            swal(
                ConstantsService.getSwalConfig(
                    'Close without saving?',
                    'Do you want to delete unsaved data?'
                ),
                () => this.onClose.emit()
            );
            this.propertyService.selectedBuildings = [];
        } else {
            this.onClose.emit();
        }
    }

    onSubmit(form: NgForm) {
        if (form.invalid)
            return toastr.error('Property name should not be blank.');
        this.setCompany(form.value.companyId);
        const propertyData = new Property(Object.assign(this.data, form.value));
        let buildingsWithCorporations = '';
        if (this.tabId === 'propertyGroup') {
            this.propertyService.selectedBuildings.forEach(element => {
                const match = this.propertyService.buildingsList.find(b => b.id == element.id);
                if (match && match.corporationNumber &&
                    (this.isEditMode ? this.propertyService.currentCorporationNumber.id != match.corporationNumber : true)) {
                    buildingsWithCorporations += element.name + ', ';
                }
            });
        }
        if (buildingsWithCorporations) {
            swal(ConstantsService.getSwalConfig('Are you sure that you want to change corporation?', `${buildingsWithCorporations.slice(0, -2)} already have a corporation assigned to them. If you continue, they are going to be moved to a new corporation.`),
                () => {
                    this.createItem(propertyData).subscribe(
                        () => {
                            this.updateView('Corporation created.');
                            this.onClose.emit();
                        },
                        error => {
                            this.api.errorHandler(error, 'Corporation creation failed.');
                            this.disabledSubmit = false;
                        }
                    );
                }
            );
        } else {
            this.createItem(propertyData).subscribe(
                () => {
                    this.updateView(`${this.tabId === 'propertyGroup' ? 'Corporation' : 'Property'} created.`);
                    this.onClose.emit();
                },
                error => {
                    this.api.errorHandler(error, `${this.tabId === 'propertyGroup' ? 'Corporation' : 'Property'} creation failed.`);
                    this.disabledSubmit = false;
                }
            );
        }
    }

    updateView(msg: string): void {
        toastr.success(msg);
    }

    createItem(data: Object) {
        this.disabledSubmit = true;
        const tabId = this.propertyService.urlService.queryParams['tab'];

        if (tabId === 'propertyGroup') {
            delete (<any>data).contacts;
            data['buildings'] = this.propertyService.selectedBuildings.map(element => (element.id));
        }

        if (tabId === 'propertyGroup' && this.propertyService.currentCorporationNumber && this.propertyService.currentCorporationNumber.id) {
            return this.propertyService
                .update(this.propertyService.currentCorporationNumber.id, data)
                .map((res: Property) => {
                    this.propertyService.get();
                });
        } else {
            return this.propertyService.create(data).map((res: Property) => {
                if (tabId !== 'propertyGroup') {
                    const data = {
                        buildingId: res.id
                    };
                    this.propertyService.createCurrentServices(data).subscribe(
                        () => this.propertyService.get(),
                        error => {
                            this.api.errorHandler(error, 'Failed to create building services');
                            this.propertyService.get();
                        }
                    );
                }
            });
        }
    }

    resetPhones(contacts) {
        this.data.contacts = JSON.parse(JSON.stringify(contacts));
    }

    setCompany(companyId) {
        this.data.companyName =
            companyId &&
            this.propertyService.companies.find(item => Number(item.id) === Number(companyId)).name;
    }

    onContactChanged(event) {
        if (event.length > 0)
            this.contactsTouched = true;
        else
            this.contactsTouched = false;
    }

    getAddress(address): void {
        this.addressSuggestions = [];
        this.data.longitude = null;
        this.data.latitude = null;
        if (address) {
            const url = `${ConstantsService.googleMapsApi}geocode/json?address=${address}&key=${ConstantsService.googleMapsApiKey}`;
            this.http.get(url).subscribe(
                response => {
                    const body = JSON.parse((<any>response)._body);
                    this.addressSuggestions = body.results || [];
                },
                error => this.api.errorHandler(error, 'Failed to get addresss data from Google Maps API.')
            );
        }
    }

    changeAutocompleteValue(event) {
        (<any>$('#address')[0]).value = event.option.value.formatted_address;
        this.data.address = event.option.value.formatted_address;
        this.data.longitude = event.option.value.geometry.location.lng;
        this.data.latitude = event.option.value.geometry.location.lat;
    }

    placeMarker(event) {
        this.addressTimeout = setTimeout(() => {
            const url = `${ConstantsService.googleMapsApi}geocode/json?latlng=${event.coords.lat},${event.coords.lng}&key=${ConstantsService.googleMapsApiKey}`;
            this.http.get(url).subscribe(
                response => {
                    const body = JSON.parse((<any>response)._body);
                    // this.data.address = body.results[0].formatted_address;
                    this.data.longitude = body.results[0].geometry.location.lng;
                    this.data.latitude = body.results[0].geometry.location.lat;
                },
                error => this.api.errorHandler(error, 'Failed to get addresss data from Google Maps API.')
            );
        }, 500);
    }

    mapDblClick() {
        if (this.addressTimeout)
            clearTimeout(this.addressTimeout);
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.close(this.form), 100);
    }

    public isUserAdmin() {
        return this.propertyService.account.isUserCompanyAdmin() || this.propertyService.account.isUserAdmin();
    }

    ngOnDestroy(): void {
        this.$destroy.next();
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/admin/property/property-modal/property-modal.component.ts